import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Apple, Check, ImageUp, Smartphone, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AppMark } from '@/components/layout/app-switcher';
import { AndroidForm, emptyAndroid, emptyIos, IosForm, type AndroidDraft, type IosDraft } from '@/components/credentials/credential-forms';
import { FormField } from '@/components/shared/form-field';
import { LoadingButton } from '@/components/shared/loading-button';
import { PageHeader } from '@/components/shared/page-header';
import { SectionCard } from '@/components/shared/section-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { LOGO_ACCEPT, LOGO_SIZE, logoFromFile } from '@/lib/logo';
import { notifyError } from '@/lib/toast';
import type { AppDetail } from '@/lib/types';
import { urlProblem } from '@/lib/url';
import { cn } from '@/lib/utils';

type Drafts = { android: AndroidDraft | null; ios: IosDraft | null };
const STEPS = ['Basics', 'Credentials'] as const;

export function CreateApp() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [logo, setLogo] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const urlError = urlProblem(url.trim());
  const [drafts, setDrafts] = useState<Drafts>({ android: null, ios: null });

  const create = useMutation({
    mutationFn: () =>
      api<AppDetail>('/admin/v1/apps', {
        method: 'POST',
        body: {
          name, description: description || undefined, url: url.trim() || undefined, logo: logo ?? undefined,
          ...(drafts.android?.service_account_json ? { android: drafts.android } : {}),
          ...(drafts.ios?.private_key_p8 ? { ios: drafts.ios } : {}),
        },
      }),
    onSuccess: (app) => {
      void qc.invalidateQueries({ queryKey: ['apps'] });
      nav(`/apps/${app.id}/credentials`); // land on the verify buttons
    },
  });

  const setDraft = (patch: Partial<Drafts>) => setDrafts((d) => ({ ...d, ...patch }));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="New application" description="Set up an app in two steps. Credentials can also be added later." />
      <ol className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const n = i + 1;
          return (
            // Only steps followed by a connector stretch, so the last step ends at the right edge.
            <li key={s} className={cn('flex items-center gap-2', n < STEPS.length && 'flex-1')}>
              <span className={cn('grid size-7 shrink-0 place-items-center rounded-full border text-xs font-medium',
                step === n ? 'border-primary bg-primary text-primary-foreground' : step > n ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground')}>
                {step > n ? <Check className="size-3.5" /> : n}
              </span>
              <span className={cn('text-sm', step === n ? 'font-medium' : 'text-muted-foreground')}>{s}</span>
              {n < STEPS.length && <span className={cn('h-px flex-1', step > n ? 'bg-primary' : 'bg-border')} />}
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <SectionCard title="Basics" description="How the app appears in this console.">
          <div className="grid gap-4">
            <FormField label="Name" htmlFor="name"><Input id="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="My Mobile App" /></FormField>
            <FormField label="Description (optional)" htmlFor="desc"><Input id="desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this app is and who uses it" /></FormField>
            <FormField label="Application URL (optional)" htmlFor="app-url" hint="The application's own website, e.g. https://myshop.com." error={urlError ?? undefined}>
              <Input id="app-url" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://myshop.com" maxLength={2048} />
            </FormField>
            <FormField label="Logo (optional)">
              <div className="flex flex-wrap items-center gap-4">
                <AppMark name={name || '?'} logo={logo} className="size-14 text-xl" />
                <input
                  ref={logoInput}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  // Resized in the browser now; the API uploads it to storage when the app is created.
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void logoFromFile(f).then(setLogo, notifyError); }}
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => logoInput.current?.click()}><ImageUp /> {logo ? 'Replace' : 'Upload logo'}</Button>
                  {logo && <Button type="button" variant="ghost" size="sm" onClick={() => setLogo(null)}><X /> Remove</Button>}
                </div>
                <p className="w-full text-xs text-muted-foreground">PNG, JPG, WebP, GIF or SVG. Resized to {LOGO_SIZE}×{LOGO_SIZE}; a square image looks best.</p>
              </div>
            </FormField>
            <div className="flex justify-end"><Button disabled={!name.trim() || !!urlError} onClick={() => setStep(2)}>Next</Button></div>
          </div>
        </SectionCard>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Credentials are optional now — you can add or replace them later. They are encrypted on save and never shown again.</p>
          <SectionCard title="Push credentials">
            <div className="grid gap-5">
              <PlatformToggle icon={<Smartphone className="size-4" />} label="Android (FCM)" on={!!drafts.android} onToggle={(on) => setDraft({ android: on ? emptyAndroid() : null })}>
                {drafts.android && <AndroidForm value={drafts.android} onChange={(v) => setDraft({ android: v })} />}
              </PlatformToggle>
              <PlatformToggle icon={<Apple className="size-4" />} label="iOS (APNs)" on={!!drafts.ios} onToggle={(on) => setDraft({ ios: on ? emptyIos() : null })}>
                {drafts.ios && <IosForm value={drafts.ios} onChange={(v) => setDraft({ ios: v })} />}
              </PlatformToggle>
            </div>
          </SectionCard>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <LoadingButton loading={create.isPending} onClick={() => create.mutate()}>Create application</LoadingButton>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformToggle({ icon, label, on, onToggle, children }: { icon: ReactNode; label: string; on: boolean; onToggle(on: boolean): void; children: ReactNode }) {
  return (
    <div className="grid gap-3 rounded-lg border p-4">
      <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-medium">
        <span className="inline-flex items-center gap-2">{icon} {label}</span>
        <Switch checked={on} onCheckedChange={onToggle} />
      </label>
      {children}
    </div>
  );
}
